import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import * as analytics from '../services/analytics';

interface AuthState {
  token: string | null;
  userId: string | null;
  username: string | null;
  picture: string | null;
  subscriptionStatus: string;
  isAuthenticated: boolean;
  isPro: boolean;
}

interface AuthContextType extends AuthState {
  login: (data: { token: string; userId: string; username: string; picture: string; subscriptionStatus: string }) => void;
  logout: () => void;
  refreshSubscription: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>(() => {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    const username = localStorage.getItem('username');
    const picture = localStorage.getItem('picture');
    const subscriptionStatus = localStorage.getItem('subscriptionStatus') || 'free';

    return {
      token,
      userId,
      username,
      picture,
      subscriptionStatus,
      isAuthenticated: !!token,
      isPro: subscriptionStatus === 'active' || subscriptionStatus === 'trialing',
    };
  });

  const login = useCallback((data: { token: string; userId: string; username: string; picture: string; subscriptionStatus: string }) => {
    localStorage.setItem('token', data.token);
    localStorage.setItem('userId', data.userId);
    localStorage.setItem('username', data.username);
    localStorage.setItem('picture', data.picture);
    localStorage.setItem('subscriptionStatus', data.subscriptionStatus);

    setState({
      ...data,
      isAuthenticated: true,
      isPro: data.subscriptionStatus === 'active' || data.subscriptionStatus === 'trialing',
    });

    analytics.identify({
      userId: data.userId,
      name: data.username,
      subscription: data.subscriptionStatus,
    });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('username');
    localStorage.removeItem('picture');
    localStorage.removeItem('subscriptionStatus');

    setState({
      token: null,
      userId: null,
      username: null,
      picture: null,
      subscriptionStatus: 'free',
      isAuthenticated: false,
      isPro: false,
    });
  }, []);

  const refreshSubscription = useCallback(async () => {
    if (!state.token || !state.userId) return;
    try {
      const res = await api.get(`/subscribe/status?userId=${state.userId}`);
      const subStatus = res.data.subscriptionStatus || 'free';
      localStorage.setItem('subscriptionStatus', subStatus);
      setState(prev => ({
        ...prev,
        subscriptionStatus: subStatus,
        isPro: subStatus === 'active' || subStatus === 'trialing',
      }));
    } catch (err) {
      console.error('Error refreshing subscription:', err);
    }
  }, [state.token, state.userId]);

  useEffect(() => {
    if (state.isAuthenticated && state.userId) {
      analytics.identify({
        userId: state.userId,
        name: state.username ?? undefined,
        subscription: state.subscriptionStatus,
      });
    }
  }, [state.isAuthenticated, state.userId, state.username, state.subscriptionStatus]);

  useEffect(() => {
    const handleStorage = () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setState(prev => ({ ...prev, token: null, isAuthenticated: false }));
      }
    };
    window.addEventListener('storage', handleStorage);
    window.addEventListener('auth:logout', logout);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('auth:logout', logout);
    };
  }, [logout]);

  return (
    <AuthContext.Provider value={{ ...state, login, logout, refreshSubscription }}>
      {children}
    </AuthContext.Provider>
  );
};
